import { MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';

interface FilterSelectorProps {
  label: string;
  options: string[];
  selectedOptions: string[];
  setSelectedOptions: (options: string[]) => void;
}

/**
 * Generic component for selecting multiple options from a list
 * @param props Label to show upon the selector, options and selected options
 */
const FilterSelectorComponent = (props: FilterSelectorProps) => {
  const { t } = useTranslation();
  const { label, options, selectedOptions, setSelectedOptions } = props;
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const firstOptionRef = useRef<HTMLLIElement | null>(null);
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', marginBottom: '-0.5rem' }}
      >
        {t(label)}
      </Typography>
      <Select
        multiple
        value={selectedOptions}
        onChange={(event) => {
          const value = event.target.value as string[];
          if (value.includes(t('select_all'))) {
            if (selectedOptions.length === options.length) {
              setSelectedOptions([]);
            } else {
              setSelectedOptions(options);
            }
          } else {
            setSelectedOptions(value);
          }
        }}
        displayEmpty
        renderValue={(value) => {
          if (value.length === 0) {
            return (
              <Typography color="gray">{t('selector_placeholder')}</Typography>
            );
          }
          return value.join(', ');
        }}
        MenuProps={MenuProps}
      >
        <MenuItem
          key={t('select_all')}
          value={t('select_all')}
          onClick={(event: any) => {
            if (selectedOptions.length === options.length) {
              setSelectedOptions([]);
            } else {
              setSelectedOptions(options);
            }
            // Prevents the selector menu from scrollin down when the select all option is clicked
            setTimeout(() => {
              firstOptionRef.current?.focus();
            }, 0);
          }}
        >
          {selectedOptions.length !== options.length
            ? t('select_all')
            : t('deselect_all')}
        </MenuItem>
        {options.map((option: string, index: number) => (
          <MenuItem
            key={option}
            value={option}
            ref={index === 0 ? firstOptionRef : null}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
    </>
  );
};

export default FilterSelectorComponent;
